import React from "react";
import styled from "styled-components";
import { Button, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useFieldArray, useForm } from "react-hook-form";
import { queryCache, useMutation } from "react-query";
import { joiResolver } from "@hookform/resolvers";
import Joi from "@hapi/joi";
import { TInput } from "../form/Field/TInput";
import { apiAxios } from "services/HttpServices";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import BindingSecondOption from "../job/add/form/bindingSecondOption";
import { CustomLinkButton } from "../job/add/form/HotStampingRadio";
import { StyledContainer } from "../job/add/form/coverSection";

const createBinding = async (binding) => {
  try {
    const res = await apiAxios.post(`/jobs/bindings`, binding);
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

const secondOption = Joi.object().keys({
  name: Joi.string().required(),
  value: Joi.array().items(
    Joi.object().keys({ value: Joi.string().required() })
  ),
});

const bindingSchema = Joi.object({
  name: Joi.string().required(),
  second_option: Joi.array().items(secondOption),
});

function AddBindingModal(props) {
  const { visible, setBindingModalVisible } = props;
  const [error, setError] = React.useState(null);
  const [secondOptionAppend, setSecondOptionAppend] = React.useState([]);
  const {
    state: notificationState,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);
  const { control, handleSubmit, reset, getValues, errors, watch } = useForm({
    resolver: joiResolver(bindingSchema),
    defaultValues: {
      name: null,
      second_option: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "second_option",
  });

  const onSubmit = async (data) => {
    setError("");
    setLoading("createBinding");
    if (data.second_option?.length > 0) {
      data.second_option = data.second_option.map((secondOption) => {
        const formattedValue = [];
        if (secondOption.value && secondOption.value.length > 0) {
          secondOption.value.map((value) => formattedValue.push(value.value));
        }
        return {
          ...secondOption,
          value: formattedValue,
        };
      });
    }

    try {
      await mutateBindings(data);
      finishLoading("createBinding");
      setBindingModalVisible(false);
      reset();
    } catch (error) {
      finishLoading("createBinding");
      reset();
    }
  };

  //Mutation
  const [mutateBindings] = useMutation(createBinding, {
    onSuccess: (newBinding) => {
      queryCache.setQueryData(["bindings"], function (oldData) {
        return [
          ...oldData,
          {
            ...newBinding,
            second_option: newBinding.second_option
              ? JSON.parse(newBinding.second_option)
              : null,
          },
        ];
      });
      reset();
      setBindingModalVisible(false);
    },
    onError: (e) => {
      setError(e);
    },
  });

  const handleCloseModal = () => {
    setBindingModalVisible(false);
    reset();
  };

  return (
    <Modal
      title="Add new binding"
      visible={visible}
      onCancel={handleCloseModal}
      onOk={handleSubmit(onSubmit)}
      confirmLoading={notificationState.loading.createBinding}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <TInput
          errors={errors}
          label="Binding Name*"
          name="name"
          placeholder="Name"
          autoFocus={true}
          propsInput={{
            control: control,
          }}
        />
        {fields.map((item, index) => (
          <StyledContainer key={item.id}>
            <TInput
              errors={errors}
              label="Second option name"
              name={`second_option[${index}].name`}
              defaultValue={item.name}
              autoFocus={true}
              placeholder="Name"
              propsInput={{
                control: control,
              }}
            />
            <BindingSecondOption
              secondOptionIndex={index}
              control={control}
              errors={errors}
              secondOptionAppend={secondOptionAppend}
              watch={watch}
            />
            <StyledRemoveButton
              danger
              icon={<DeleteOutlined />}
              size="medium"
              onClick={() => remove(index)}
            >
              Remove
            </StyledRemoveButton>
          </StyledContainer>
        ))}

        <CustomLinkButton
          onClick={() => {
            append({
              name: null,
              second_option: [
                {
                  name: "huhu",
                  value: [{ value: null }],
                },
              ],
            });
          }}
        >
          Add Second Option
        </CustomLinkButton>
      </form>
    </Modal>
  );
}

const StyledRemoveButton = styled(Button)`
  margin-top: ${(props) => props.theme.spacing[2]};
  border-color: ${(props) => props.theme.colors.grey[400]};
  background-color: ${(props) => props.theme.colors.grey[100]};
  color: ${(props) => props.theme.colors.grey[500]};

  :hover,
  :active,
  :visited {
    color: ${(props) => props.theme.colors.red[600]};
    border-color: ${(props) => props.theme.colors.red[600]};
  }
`;

export default AddBindingModal;
